/**
 * Grand Quack Auto — duck-volution cinematic UI helpers.
 *
 * Owns the DOM/audio side of the level-up sequence: white flash overlay,
 * giant DUCK-VOLUTION! card, ascending arpeggio, pitch-up quack, and the
 * persistent "Lv.N — TIER NAME" badge in the HUD.
 *
 * Extracted from duckEvolutionSystem.ts so the system file stays under the
 * project's 200-line cap (CLAUDE.md). Pure presentation — no scene/physics.
 */

import { DUCK_TIERS, type DuckTier } from '../data/duckTiers';
import { getCtx, getDestination, getHtmlVolume } from '../utils/audioMixer';

let nameEl: HTMLElement | null = null;

export function showEvoFlash(): void {
  const flash = document.createElement('div');
  flash.id = 'evo-flash';
  document.body.appendChild(flash);
  setTimeout(() => flash.remove(), 800);
}

export function showEvoText(oldName: string, newName: string): void {
  const currentIdx = DUCK_TIERS.findIndex(d => d.name === newName);
  const currentTier = DUCK_TIERS[currentIdx];

  // Build duck chain — absurdly large emojis showing the full progression.
  const duckChain = DUCK_TIERS.map((t, i) => {
    const isCurrent = i === currentIdx;
    const isPast = i < currentIdx;
    const baseSize = 28 + i * 12; // 28px → 100px — MASSIVE progression
    const size = isCurrent ? baseSize + 16 : baseSize;
    const cls = isCurrent ? 'evo-icon-current' : isPast ? 'evo-icon-past' : 'evo-icon-future';
    return `<span class="evo-icon ${cls}" style="font-size:${size}px">${t.icon}</span>`;
  }).join('');

  const el = document.createElement('div');
  el.id = 'evo-text';
  el.innerHTML = `
    <div class="evo-title">DUCK-VOLUTION!</div>
    <div class="evo-big-icon">${currentTier?.icon ?? '🦆'}</div>
    <div class="evo-new-name">${newName.toUpperCase()}</div>
    <div class="evo-names">
      <span class="evo-old">${oldName.toUpperCase()}</span>
      <span class="evo-arrow">→</span>
      <span class="evo-new">${newName.toUpperCase()}</span>
    </div>
    <div class="evo-duck-chain">${duckChain}</div>
  `;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 4200);
}

export function playEvoQuack(): void {
  try {
    const q = new Audio('/sounds/quack.mp3');
    q.playbackRate = 1.5; // higher pitch = exciting
    q.volume = getHtmlVolume(1.0);
    q.play().catch(() => {});
  } catch { /* audio not available */ }
}

export function playLevelUpSound(): void {
  try {
    const ctx = getCtx();
    const now = ctx.currentTime;
    // Ascending arpeggio.
    [523, 659, 784, 1047].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, now + i * 0.08);
      gain.gain.linearRampToValueAtTime(0.2, now + i * 0.08 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.08 + 0.15);
      osc.connect(gain).connect(getDestination());
      osc.start(now + i * 0.08);
      osc.stop(now + i * 0.08 + 0.2);
    });
  } catch { /* audio not available */ }
}

export function updateNameDisplay(tier: DuckTier, index: number): void {
  if (!nameEl) {
    nameEl = document.getElementById('hud-duck-name');
    if (!nameEl) {
      // Create the element in the HUD.
      const hud = document.getElementById('hud');
      if (hud) {
        nameEl = document.createElement('div');
        nameEl.id = 'hud-duck-name';
        nameEl.style.cssText =
          'position:absolute;top:50px;left:16px;color:#ffd700;' +
          'font-family:Bangers,cursive;font-size:18px;letter-spacing:2px;' +
          'text-shadow:2px 2px 0 #000,0 0 8px rgba(255,215,0,0.4);';
        hud.appendChild(nameEl);
      }
    }
  }
  if (nameEl) {
    const stars = '\u2605'.repeat(Math.min(index, 3));
    const tierNum = `Lv.${index + 1}`;
    nameEl.innerHTML =
      `<span style="background:#333;padding:2px 6px;border-radius:4px;font-size:11px;margin-right:6px">${tierNum}</span>` +
      `${stars ? stars + ' ' : ''}${tier.name.toUpperCase()}`;
    nameEl.style.color = '#44ff44';
    setTimeout(() => { if (nameEl) nameEl.style.color = '#ffd700'; }, 1500);
  }
}
